import { errorMessage } from '../utils/errorMessage';

/**
 * Post-sign-in wiring, run so that it CANNOT be mistaken for a failed
 * sign-in.
 *
 * `initializeAuth` used to do this:
 *
 *     authService.getUser()
 *       .then(user => { set({ user }); connectSocket(...); registerPlanListener(...); syncLinkedAccountsFromIdp(); })
 *       .catch(() => { clearTokens(); ...sign the user out... })
 *
 * Everything in that `.then()` is downstream of the fetch but UPSTREAM of the
 * catch, so a throw from any of those three — a socket constructed against a
 * malformed region URL, an undefined read in a listener, a rejected IdP
 * linked-accounts sync — arrived at the catch indistinguishable from "the
 * server says you are not signed in", and destroyed the refresh token. The
 * session was already valid at that point; `getUser()` had returned a user.
 *
 * Two things fix that, and both matter:
 *  1. these steps run in a link placed AFTER the catch, where a throw can
 *     never reach it (a rejection only ever travels forwards);
 *  2. each step is isolated here, so one failing step does not skip the
 *     others — a socket that will not connect must not also cost you the
 *     plan listener.
 *
 * Steps are passed in as `[name, fn]` rather than imported, because the real
 * callers close over authStore's module state (`planListenerRegistered`) and
 * over `set`. Keeping this module ignorant of them is what makes it testable
 * without standing up the whole store.
 */

/**
 * Run each step, swallowing and logging its failure.
 *
 * Async steps are handled too: a returned promise gets its own `.catch`, so a
 * rejection cannot escape as an unhandled rejection either. Nothing is
 * awaited — these are fire-and-forget wiring, and the caller must not be made
 * to wait on a socket handshake before the app is usable.
 *
 * @param {Array<[string, Function]>} steps
 */
export function runSessionEffects(steps) {
  for (const [name, fn] of steps || []) {
    if (typeof fn !== 'function') continue;
    try {
      const result = fn();
      if (result && typeof result.then === 'function') {
        result.then(undefined, (err) => logStepFailure(name, err));
      }
    } catch (err) {
      logStepFailure(name, err);
    }
  }
}

function logStepFailure(name, err) {
  // console, not the notification store: these are our own bugs, and the user
  // can do nothing about them. What they must NOT do is get signed out.
  console.warn(`[auth] post-sign-in step "${name}" failed:`, errorMessage(err, 'unknown error'));
}
